import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleCheck, Loader2 } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { cn } from '@shared/utils/utils';
import { THREE_PL_EXCEL_REPORTS } from '../../models/reports';
import { formatShipDateRangeForDisplay } from '../../utils/formatDisplayDate';
import { isExportInProgress, type StoredThreePlExport } from '../../utils/pendingExportStorage';

interface ExportProgressPanelProps {
  activeExport: StoredThreePlExport;
  onCancel: () => void;
}

type StepId = 'queue' | 'process' | 'download';

const STEPS: StepId[] = ['queue', 'process', 'download'];

function getActiveStepIndex(status: StoredThreePlExport['status']): number {
  if (status === 'starting' || status === 'queued') return 0;
  if (status === 'processing') return 1;
  if (status === 'completed') return 2;
  return 0;
}

export function ExportProgressPanel({ activeExport, onCancel }: ExportProgressPanelProps) {
  const { t, i18n } = useTranslation('threePlExcelReportsModule');

  const contextLabel = useMemo(() => {
    if (activeExport.reportId === 'active_clients') {
      return t('progress.active_clients_context');
    }

    if (activeExport.invoiceId != null) {
      const invoice = activeExport.invoiceLabel || `#${activeExport.invoiceId}`;
      return t('progress.invoice_label', { invoice });
    }

    if (!activeExport.dateFrom || !activeExport.dateTo) {
      return null;
    }

    const displayRange = formatShipDateRangeForDisplay(activeExport.dateFrom, activeExport.dateTo, i18n.language);
    return activeExport.dateFrom === activeExport.dateTo
      ? t('progress.date_range_single', { date: displayRange.from })
      : t('progress.date_range', { from: displayRange.from, to: displayRange.to });
  }, [activeExport, i18n.language, t]);

  const reportDef = THREE_PL_EXCEL_REPORTS.find((r) => r.id === activeExport.reportId);
  const inProgress = isExportInProgress(activeExport.status);
  const activeStep = getActiveStepIndex(activeExport.status);

  const statusLabel = useMemo(() => {
    switch (activeExport.status) {
      case 'starting':
        return t('progress.status_starting');
      case 'queued':
        return t('progress.status_queued');
      case 'processing':
        return t('progress.status_processing');
      default:
        return t('progress.status_processing');
    }
  }, [activeExport.status, t]);

  return (
    <div
      className="rounded-lg border border-border bg-card px-4 py-3 transition-colors"
      data-testid="three-pl-export-progress-panel"
      role="status"
      aria-live="polite"
      aria-busy={inProgress}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="text-sm font-medium text-foreground">
            {reportDef ? t(reportDef.titleKey) : t('progress.title')}
          </p>
          <p className="text-sm text-muted-foreground">{statusLabel}</p>
          <p className="text-xs text-muted-foreground">{contextLabel}</p>
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={onCancel}
          data-testid="three-pl-export-cancel"
        >
          {t('progress.cancel')}
        </Button>
      </div>

      <ol className="mt-3 grid grid-cols-3 gap-2 border-t border-border pt-3" aria-label={t('progress.title')}>
        {STEPS.map((stepId, index) => {
          const isDone = index < activeStep;
          const isCurrent = index === activeStep;

          return (
            <li
              key={stepId}
              className={cn(
                'flex items-center justify-center gap-1.5 text-xs sm:text-sm',
                isCurrent ? 'font-medium text-foreground' : 'text-muted-foreground',
              )}
            >
              {isDone ? (
                <CircleCheck className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
              ) : isCurrent && inProgress ? (
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
              ) : (
                <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-border bg-muted/30" aria-hidden />
              )}
              <span>{t(`progress.steps.${stepId}`)}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
